import type {NavigateOptions, PrefetchOptions} from 'next/dist/shared/lib/app-router-context.shared-runtime';
import {usePathname, useRouter as useNavigation, useSearchParams} from 'next/navigation';
import {useCallback, useContext, useMemo} from 'react';

import {FormDirtyContext} from '@/components/FormContextWrapper';

import {isAnchorOfCurrentUrl, isHashAnchor} from '../helpers';
import {useGlobalLoaderContext} from './contextHooks';

function isSameRoute(pathName: string, currentHref: string) {
  return (
    isAnchorOfCurrentUrl(process.env.NEXT_PUBLIC_BASEPATH + currentHref, process.env.NEXT_PUBLIC_BASEPATH + pathName) ||
    isHashAnchor(process.env.NEXT_PUBLIC_BASEPATH + currentHref, process.env.NEXT_PUBLIC_BASEPATH + pathName) ||
    pathName === currentHref ||
    window.location.pathname + window.location.search === currentHref
  );
}
export default function useRouter() {
  const {setIsPageLoading} = useGlobalLoaderContext();
  const {isFormDirty, setCloseHandler, setShowModal} = useContext(FormDirtyContext);
  const {push, replace, prefetch, back, forward} = useNavigation();
  const params = useSearchParams();
  const searchParams = new URLSearchParams(params.toString());
  const pathName = usePathname() + (searchParams.size > 0 ? '?' + searchParams.toString() : '');

  const performRouteChange = useCallback(
    (callback: () => void) => {
      if (isFormDirty) {
        setShowModal(true);
        setCloseHandler(() => {
          callback();
          return Promise.resolve();
        });
      } else {
        callback();
      }
    },
    [isFormDirty, setCloseHandler, setShowModal],
  );

  const routerPush = useCallback(
    (href: string, options?: NavigateOptions) => {
      const routerPush = () => {
        if (isSameRoute(pathName, href.replace('/en/', '/'))) setIsPageLoading(false);
        else setIsPageLoading(true);
        push(href, options);
        return Promise.resolve();
      };
      performRouteChange(routerPush);
    },
    [push, setIsPageLoading, pathName, performRouteChange],
  );

  const routerReplace = useCallback(
    (href: string, options?: NavigateOptions) => {
      const routerReplace = () => {
        if (isSameRoute(pathName, href.replace('/en/', '/'))) setIsPageLoading(false);
        else setIsPageLoading(true);
        replace(href, options);
        return Promise.resolve();
      };
      performRouteChange(routerReplace);
    },
    [replace, setIsPageLoading, pathName, performRouteChange],
  );

  const routerBack = useCallback(() => {
    const routerBack = () => {
      setIsPageLoading(true);
      back();
      return Promise.resolve();
    };
    performRouteChange(routerBack);
  }, [back, setIsPageLoading, performRouteChange]);

  const routerForward = useCallback(() => {
    const routerForward = () => {
      setIsPageLoading(true);
      forward();
      return Promise.resolve();
    };

    performRouteChange(routerForward);
  }, [forward, setIsPageLoading, performRouteChange]);

  const routerPrefetch = useCallback(
    (href: string, options?: PrefetchOptions) => {
      prefetch(href, options);
    },
    [prefetch],
  );

  const routerObject = useMemo(
    () => ({push: routerPush, replace: routerReplace, back: routerBack, forward: routerForward, prefetch: routerPrefetch}),
    [routerPush, routerReplace, routerBack, routerForward, routerPrefetch],
  );

  return routerObject;
}
